import { hooks } from "botframework-webchat";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { hide } from "components/Home/homeVisibilitySlice";
import { ModalType } from "constants/enums";
import { openOrCloseModal } from "shared/components/Modal/modalActionsSlice";
import api from "shared/functions/api";

import { SkillIcon } from "components/skills/SkillChips";
import SkillMdConsole from "components/skills/SkillMdConsole";
import { SkillInfo } from "components/skills/skillsSlice";
import InlineChart from "components/webChat/InlineChart";
import MessageAttachments from "shared/components/MessageAttachments/MessageAttachments";
import { splitMessageAttachments } from "shared/functions/fileToText";

import { getFeedbackHistory, History, storeFeedback } from "./createFeedbackHistory";
import { dispatchToWebChat } from "./webChatStore";

const BotActivityDecorator = ({ activityID, text, sqlQueries, chartSpec, skillMd, children }: any) => {
  const [actionStatus, setActionStatus] = useState("Idle");
  const [delayDuration, setDelayDuration] = useState(5000);
  const { usePostActivity, useSetNotification, useDismissNotification } = hooks;
  const dispatch = useDispatch();
  const notification = useSetNotification();
  const dismissNotification = useDismissNotification();

  const postActivity = usePostActivity();
  let itemsFeedbackHistory = getFeedbackHistory();

  const handleUpvoteButton = useCallback(() => {
    postActivity({
      type: "messageReaction",
      activityID: activityID,
      reactionsAdded: [{ id: activityID, type: "like" }],
    });
  }, [activityID, postActivity]);

  const handleDownvoteButton = useCallback(() => {
    localStorage.setItem("activityId", activityID);
    dispatch(openOrCloseModal({ isOpen: true, modalType: ModalType.Feedback }));
  }, [activityID, postActivity]);

  const showReactions = () => {
    const message = itemsFeedbackHistory.find(
      (history: History) => history.messageId === activityID
    );
    if (message) {
      if (message.reactionType === "like") return false;
      if (message.feedbackId !== "") return false;
    }
    return true;
  };

  const copyTextToClipboard = useCallback(() => {
    notification({
      id: "Tet",
      level: "info",
      alt: "Copied",
      message: "Copied",
    });
    setActionStatus("Action in progress...");

    // Use setTimeout to simulate a delayed action
    setTimeout(() => {
      dismissNotification("Tet");
      setActionStatus("Action completed!");
    }, delayDuration);

    if (text) {
      navigator.clipboard.writeText(text.replace(/(<([^>]+)>)/gi, ""));

      const tooltip = document.getElementById("myTooltip");
      if (tooltip) tooltip.innerHTML = "Copied";
    }
  }, [text]);

  // Log visual de debug: inyecta los SQL queries del mensaje como
  // una actividad local del transcript. No viaja a Direct Line ni
  // al bot, no se persiste en Cosmos y desaparece al recargar. El
  // botón solo existe cuando el bot mandó sqlQueries en el
  // channelData (gated por SQL_DEBUG=On en el backend, nunca en
  // productivo).
  const handleShowSqlQueries = useCallback(() => {
    if (!sqlQueries || sqlQueries.length === 0) return;
    const sqlText = sqlQueries
      .map(
        (query: string, index: number) =>
          `**Query ${index + 1} of ${sqlQueries.length}:**\n\`\`\`sql\n${query}\n\`\`\``
      )
      .join("\n\n");
    dispatchToWebChat({
      type: "DIRECT_LINE/QUEUE_INCOMING_ACTIVITY",
      payload: {
        activity: {
          type: "message",
          id: `sql-debug-${activityID}-${Date.now()}`,
          timestamp: new Date().toISOString(),
          from: { id: "sql-debug-log", role: "bot" },
          text: `🛠️ **SQL debug log**\n\n${sqlText}`,
          textFormat: "markdown",
          channelData: { isReactionable: "No", sqlDebugLog: true },
        },
      },
    });
  }, [activityID, sqlQueries]);

  return (
    <div className="botActivityDecorator">
      <div className="botActivityDecorator__content">
        <div className="custom-message-container">
          <img
            src="static/images/chat/BotAvatar.png"
            alt="User Avatar"
            className="avatar-image"
          />
          <div>
            <p className="avatar-tag">LUCA Bot</p>
            <div className="bot-custom-bubble">
              <p>{children}</p>
              {/* Gráfica interactiva del turno (channelData.chartSpec,
                  camino web de create_chart): se renderiza inline
                  debajo del texto, dentro de la misma burbuja. */}
              {chartSpec && <InlineChart spec={chartSpec} />}
              {/* SKILL.md que generó /new-skill (channelData.skillMd):
                  consola con botón de copiar, debajo del texto. */}
              {skillMd && <SkillMdConsole text={skillMd} />}
            </div>
          </div>
        </div>
      </div>
      <div className="reaction-buttons-container">
        {showReactions() && (
          <>
            <button
              className="botActivityDecorator__button reaction-buttons"
              onClick={handleUpvoteButton}
            >
              <img src="static/images/icons/Like.png" />
            </button>
            <button
              className="botActivityDecorator__button reaction-buttons"
              onClick={handleDownvoteButton}
            >
              <img src="static/images/icons/Dislike.png" />
            </button>
          </>
        )}

        <div className="tooltip">
          <button
            className="botActivityDecorator__button reaction-buttons"
            onClick={copyTextToClipboard}
          >
            <span
              className="tooltiptext"
              id="myTooltip"
              style={{ marginLeft: showReactions() ? "-20px" : "-15px" }}
            >
              Copy to clipboard
            </span>
            <img src="static/images/icons/Copy.png" />
          </button>
        </div>
        {sqlQueries && sqlQueries.length > 0 && (
          <div className="tooltip">
            <button
              className="botActivityDecorator__button reaction-buttons sql-debug-button"
              onClick={handleShowSqlQueries}
            >
              <span className="tooltiptext" style={{ marginLeft: "-15px" }}>
                Show SQL queries
              </span>
              SQL
            </button>
          </div>
        )}
        <p className="ai-text">AI-generated content should be verified and used responsibly.</p>
      </div>
    </div>
  );
};

const UserActivityDecorator = ({ activityID, text, skillName, children }: any) => {
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  // Skill que este mensaje activó desde el menú "/" (channelData.skill).
  const skillCatalog: SkillInfo[] = useSelector((state: any) => state.skills?.catalog || []);
  const skill = skillName
    ? skillCatalog.find((item) => item.name === skillName)
    : undefined;
  // Los adjuntos (archivos / texto pegado) se muestran como chips
  // colapsados, claramente separados del input normal del usuario.
  const { input, attachments } = splitMessageAttachments(text || "");

  const handleSaveFavorite = useCallback(async () => {
    // A favoritos va SOLO el input del usuario, nunca el adjunto.
    if (saved || saving || !input.trim()) return;
    setSaving(true);
    try {
      const jwt = localStorage.getItem('jwt') || '';
      await api.saveFavorite(jwt, input.trim());
      setSaved(true);
      window.dispatchEvent(new CustomEvent('favorites-updated'));
    } catch (err) {
      console.error('Error saving favorite:', err);
    } finally {
      setSaving(false);
    }
  }, [input, saved, saving]);

  return (
    <>
      <div className="custom-message-container">
        <img
          src="static/images/chat/UserAvatar.png"
          alt="User Avatar"
          className="avatar-image"
        />
        <div>
          <p className="avatar-tag">You</p>
          <div className="user-custom-bubble">
            {skillName && (
              <span className="skill-chip skill-chip--message">
                <SkillIcon />
                <span className="skill-chip__command">{skill?.command || `/${skillName}`}</span>
                <span className="skill-chip__title">{skill?.title || skillName}</span>
              </span>
            )}
            <MessageAttachments attachments={attachments} />
            <p>{input}</p>
          </div>
        </div>
        <button
          className={`user-fav-btn${saved ? ' user-fav-btn--saved' : ''}`}
          onClick={handleSaveFavorite}
          title={saved ? 'Saved to favorites' : 'Save to favorites'}
          disabled={saving}
        >
          &#9733;
        </button>
      </div>
    </>
  );
};

const activityMiddleware =
  () =>
  (next: any) =>
  (...setupArgs: any) => {
    // const dispatch = useDispatch();
    const [card] = setupArgs;

    let id = card.activity.id;
    // console.log(card)
    if (card.activity) {
      if (card.activity.from.role === "user") {
        if (card.activity.type === "message") {
          // dispatch(hide());
          card.activity.activityID = id;
          return (...renderArgs: any) => (
            <>
              <UserActivityDecorator
                activityID={card.activity.id}
                key={card.activity.id}
                text={card.activity.text}
                skillName={card.activity.channelData?.skill}
              >
                {next(card)(...renderArgs)}
              </UserActivityDecorator>
            </>
          );
          // return next(card)
        }
      }

      if (card.activity.from.role === "bot") {
        if (card.activity && card.nextVisibleActivity) {
          if (card.activity.id && card.nextVisibleActivity.id) {
            storeFeedback(card.activity.id, card.nextVisibleActivity.id);
          }
        }

        if (card.nextVisibleActivity) {
          if (card.nextVisibleActivity.attachments) {
            if (
              card.nextVisibleActivity.attachments[0] &&
              card.nextVisibleActivity.attachments[0].content
            ) {
              if (card.nextVisibleActivity.attachments[0].content.actions) {
                if (
                  card.nextVisibleActivity.attachments[0].content.actions[0]
                    .title === "Provide feedback"
                ) {
                  return;
                }
              }
            }
          }
        }

        if (card.activity) {
          if (card.activity.attachments) {
            if (
              card.activity.attachments[0] &&
              card.activity.attachments[0].content
            ) {
              if (card.activity.attachments[0].content.actions) {
                if (
                  card.activity.attachments[0].content.actions[0].title ===
                  "Provide feedback"
                ) {
                  return;
                }
              }
            }
          }
        }

        // Gráfica interactiva del mensaje (camino web de
        // create_chart): en un mensaje nuevo viaja en el channelData
        // de la respuesta; en un mensaje restaurado al retomar la
        // conversación la inyecta el history del frontend (app.py,
        // campo chart_spec de Cosmos).
        const chartSpec = card.activity.channelData?.chartSpec;
        // SKILL.md de /new-skill: mismo camino que chartSpec (mensaje
        // nuevo o restaurado del historial, campo skill_md de Cosmos).
        const skillMd = card.activity.channelData?.skillMd;
        if (
          card.activity.type === "message" &&
          card.activity.text &&
          card.activity.channelData.isReactionable === "Yes"
        ) {
          if (card.activity)
            return (...renderArgs: any) => (
              <>
                <BotActivityDecorator
                  activityID={card.activity.id}
                  key={card.activity.id}
                  text={card.activity.text}
                  sqlQueries={card.activity.channelData.sqlQueries}
                  chartSpec={chartSpec}
                  skillMd={skillMd}
                >
                  {next(card)(...renderArgs)}
                </BotActivityDecorator>
              </>
            );
        } else if (card.activity.type === "message" && (chartSpec || skillMd)) {
          // Mensaje restaurado del historial (sin decorador de
          // reacciones): la gráfica y la consola del SKILL.md se
          // renderizan debajo de la burbuja default de Web Chat.
          return (...renderArgs: any) => (
            <>
              {next(card)(...renderArgs)}
              {chartSpec && <InlineChart spec={chartSpec} />}
              {skillMd && <SkillMdConsole text={skillMd} />}
            </>
          );
        } else {
          return next(...setupArgs);
        }
      }
    } else {
      return next(...setupArgs);
    }
  };

export default activityMiddleware;
